import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { Save, Trash2, Plus, X, Search, FileText, Printer, Share2, Copy, Download, Eye, EyeOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import InvoiceDocument from "@/components/InvoiceDocument";
import { buildFromFormState } from "@/components/invoice";

const today = () => new Date().toISOString().slice(0, 10);

const emptyItem = () => ({ id: crypto.randomUUID(), inventory_item_id: "", item_name: "", description: "", quantity: '', price: '', hsn_sac: "", unit: "", amount: 0 });

const calcRow = (row) => {
  const amount = Number(row.quantity || 0) * Number(row.price || 0);
  return { ...row, amount: +amount.toFixed(2) };
};

const calculateDueDate = (invoiceDate, creditPeriod) => {
  if (!invoiceDate || !creditPeriod) return "";
  const date = new Date(invoiceDate);
  date.setDate(date.getDate() + creditPeriod);
  return date.toISOString().split('T')[0];
};

export default function InvoiceEditorPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const docRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);

  // Redirect to invoices list if trying to edit (view mode is now in the list with status dropdown)
  useEffect(() => {
    if (isEdit) {
      nav("/app/invoices");
    }
  }, [isEdit, nav]);

  const { data: orgs = [] } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/organizations")).data });
  const [orgId, setOrgId] = useState("");
  const org = orgs.find((o) => o.id === orgId);

  const { data: templates = [] } = useQuery({
    queryKey: ["templates", orgId],
    queryFn: async () => (await api.get(`/organizations/${orgId}/templates`)).data,
    enabled: !!orgId,
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
  const itemColumns = Array.isArray(selectedTemplate?.item_columns) 
    ? selectedTemplate.item_columns 
    : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];

  const { data: customers = [] } = useQuery({
    queryKey: ["customers", orgId],
    queryFn: async () => (await api.get("/customers", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });

  const { data: inventory = [] } = useQuery({
    queryKey: ["inventory", orgId],
    queryFn: async () => (await api.get("/inventory", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });

  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isNewShippedCustomer, setIsNewShippedCustomer] = useState(false);
  const [shippedCustomerSearch, setShippedCustomerSearch] = useState("");
  const [showShippedCustomerDropdown, setShowShippedCustomerDropdown] = useState(false);
  const [inventorySearches, setInventorySearches] = useState({});
  const [showInventoryDropdowns, setShowInventoryDropdowns] = useState({});
  const [inventoryDropdownPositions, setInventoryDropdownPositions] = useState({});
  const inventoryDropdownRefs = useRef({});
  const inventoryDropdownContainerRefs = useRef({});
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    gst: "",
    address: "",
    city: "",
    state: "",
    state_code: "",
    country: "India",
    pincode: "",
  });
  const [newShippedCustomer, setNewShippedCustomer] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    gst: "",
    address: "",
    city: "",
    state: "",
    state_code: "",
    country: "India",
    pincode: "",
  });

  const [invoice, setInvoice] = useState({
    invoice_number: "",
    invoice_date: today(),
    due_date: "",
    customer_id: "",
    customer_id_shipped: "",
    template_id: "",
    items: [emptyItem()],
    discount: '',
    shipping: '',
    cgst: '',
    sgst: '',
    igst: '',
    roundoff: 0,
    reverse_charge: false,
    transport: '',
    e_way_bill_no: '',
    irn_number: '',
    notes: "",
    terms: "",
    status: "pending",
  });
  const [generatedInvoiceNumber, setGeneratedInvoiceNumber] = useState("");

  // Handle clone data from location state
  useEffect(() => {
    if (!isEdit && location.state?.cloneData) {
      const cloneData = location.state.cloneData;
      setOrgId(cloneData.organization_id);
      setSelectedTemplateId(cloneData.template_id || "");
      const mappedItems = cloneData.items?.length ? cloneData.items.map(item => ({
        ...item,
        id: crypto.randomUUID(), // Generate new IDs for cloned items
        inventory_item_id: item.inventory_item_id || "",
        hsn_sac: item.hsn_sac || "",
        unit: item.unit || "",
        amount: item.amount || item.total || 0,
      })) : [emptyItem()];
      
      setInvoice({
        invoice_number: "",
        invoice_date: cloneData.invoice_date || today(),
        due_date: cloneData.due_date || "",
        customer_id: cloneData.customer_id || "",
        customer_id_shipped: cloneData.customer_id_shipped || "",
        template_id: cloneData.template_id || "",
        items: mappedItems,
        discount: cloneData.discount || 0,
        shipping: cloneData.shipping || 0,
        cgst: cloneData.cgst || 0,
        sgst: cloneData.sgst || 0,
        igst: cloneData.igst || 0,
        roundoff: cloneData.roundoff || 0,
        notes: cloneData.notes || "",
        terms: cloneData.terms || "",
        status: "pending",
      });
      setIsNewCustomer(false);
      // Clear location state to prevent re-cloning on refresh
      nav(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, isEdit, nav]);

  useEffect(() => {
    if (invoice.customer_id && customers.length > 0) {
      const selectedCustomer = customers.find((c) => c.id === invoice.customer_id);
      if (selectedCustomer) {
        setCustomerSearch(selectedCustomer.name);
      }
    }
  }, [invoice.customer_id, customers]);

  useEffect(() => {
    if (invoice.customer_id_shipped && customers.length > 0) {
      const selectedCustomer = customers.find((c) => c.id === invoice.customer_id_shipped);
      if (selectedCustomer) {
        setShippedCustomerSearch(selectedCustomer.name);
      }
    }
  }, [invoice.customer_id_shipped, customers]);

  useEffect(() => {
    if (!isEdit && orgs.length && !orgId && !location.state?.cloneData) setOrgId(orgs[0].id);
  }, [orgs, isEdit, orgId, location.state]);

  useEffect(() => {
    if (!isEdit && org && !location.state?.cloneData) {
      setInvoice((prev) => ({
        ...prev,
        notes: prev.notes || org.default_notes || "",
        terms: prev.terms || org.default_terms || "",
        due_date: prev.due_date || calculateDueDate(prev.invoice_date, org.credit_period),
      }));
    }
  }, [org, isEdit, location.state]);

  useEffect(() => {
    if (!isEdit && org && invoice.invoice_date) {
      setInvoice((prev) => ({
        ...prev,
        due_date: calculateDueDate(invoice.invoice_date, org.credit_period),
      }));
    }
  }, [invoice.invoice_date, org, isEdit]);

  // Close inventory dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(inventoryDropdownRefs.current).forEach(idx => {
        const searchInput = inventoryDropdownRefs.current[idx];
        const dropdownContainer = inventoryDropdownContainerRefs.current[idx];
        
        if (searchInput && dropdownContainer) {
          // Close if click is outside both the search input and the dropdown container
          if (!searchInput.contains(event.target) && !dropdownContainer.contains(event.target)) {
            setShowInventoryDropdowns(prev => ({ ...prev, [idx]: false }));
          }
        } else if (searchInput && !searchInput.contains(event.target)) {
          // Fallback if dropdown container ref is not set
          setShowInventoryDropdowns(prev => ({ ...prev, [idx]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dropdown positions when they open or on scroll
  useEffect(() => {
    const updatePositions = () => {
      Object.keys(showInventoryDropdowns).forEach(idx => {
        if (showInventoryDropdowns[idx] && inventoryDropdownRefs.current[idx]) {
          const rect = inventoryDropdownRefs.current[idx].getBoundingClientRect();
          setInventoryDropdownPositions(prev => ({
            ...prev,
            [idx]: { top: rect.bottom + 4, left: rect.left, width: rect.width }
          }));
        }
      });
    };

    updatePositions();
    window.addEventListener('scroll', updatePositions, true);
    return () => window.removeEventListener('scroll', updatePositions, true);
  }, [showInventoryDropdowns]);

  const totals = useMemo(() => {
    const rows = invoice.items.map(calcRow);
    const subtotal = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const discountPercent = Number(invoice.discount || 0);
    const discount = subtotal * (discountPercent / 100);
    const shipping = Number(invoice.shipping || 0);
    // Convert percentage to absolute values
    const taxableAmount = subtotal - discount;
    const cgst = +(taxableAmount * (Number(invoice.cgst || 0) / 100)).toFixed(2);
    const sgst = +(taxableAmount * (Number(invoice.sgst || 0) / 100)).toFixed(2);
    const igst = +(taxableAmount * (Number(invoice.igst || 0) / 100)).toFixed(2);
    const preRoundoffTotal = +(subtotal - discount + shipping + cgst + sgst + igst).toFixed(2);
    const roundedTotal = Math.ceil(preRoundoffTotal);
    const roundoff = +(roundedTotal - preRoundoffTotal).toFixed(2);
    const total = roundedTotal;
    const totalTaxes = +(cgst + sgst + igst).toFixed(2);
    return { rows, subtotal: +subtotal.toFixed(2), discount, discountPercent, shipping, cgst, sgst, igst, totalTaxes, roundoff, total };
  }, [invoice]);

  const setItem = (idx, patch) => {
    const items = [...invoice.items];
    items[idx] = calcRow({ ...items[idx], ...patch });
    setInvoice({ ...invoice, items });
  };
  const addItem = () => setInvoice({ ...invoice, items: [...invoice.items, emptyItem()] });
  const removeItem = (idx) => setInvoice({ ...invoice, items: invoice.items.filter((_, i) => i !== idx) });

  const customer = isNewCustomer ? newCustomer : customers.find((c) => c.id === invoice.customer_id);
  const customerShipped = isNewShippedCustomer ? newShippedCustomer : customers.find((c) => c.id === invoice.customer_id_shipped);

  const previewModel = useMemo(() => {
    return buildFromFormState({
      invoice,
      totals,
      organization: org,
      customer,
      customerShipped,
      template: selectedTemplate,
      copyType: "original",
    });
  }, [invoice, totals, org, customer, customerShipped, selectedTemplate]);

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.company.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch) ||
    c.email.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const buildBody = () => ({
    organization_id: orgId,
    customer_id: invoice.customer_id,
    customer_id_shipped: invoice.customer_id_shipped || null,
    template_id: selectedTemplateId,
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date,
    reverse_charge: invoice.reverse_charge ? 1 : 0,
    transport: invoice.transport,
    e_way_bill_no: invoice.e_way_bill_no,
    irn_number: invoice.irn_number,
    items: totals.rows,
    subtotal: totals.subtotal,
    discount: totals.discountPercent,
    shipping: totals.shipping,
    cgst: totals.cgst,
    sgst: totals.sgst,
    igst: totals.igst,
    roundoff: totals.roundoff,
    total: totals.total,
    notes: invoice.notes,
    terms: invoice.terms,
    status: invoice.status,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Select an organization");
      if (!selectedTemplateId) throw new Error("Select a template");

      let customerId = invoice.customer_id;
      let customerShippedId = invoice.customer_id_shipped;

      // If new bill-to customer, create it first
      if (isNewCustomer) {
        if (!newCustomer.name) throw new Error("Bill to customer name is required");
        const createdCustomer = await (await api.post("/customers", { ...newCustomer, organization_id: orgId })).data;
        customerId = createdCustomer.id;
      }

      // If new shipped-to customer, create it first
      if (isNewShippedCustomer) {
        if (!newShippedCustomer.name) throw new Error("Ship to customer name is required");
        const createdShippedCustomer = await (await api.post("/customers", { ...newShippedCustomer, organization_id: orgId })).data;
        customerShippedId = createdShippedCustomer.id;
      }

      if (!customerId) throw new Error("Select a bill to customer");

      const body = buildBody();
      body.customer_id = customerId;
      body.customer_id_shipped = customerShippedId || null;

      if (isEdit) return (await api.put(`/invoice/${id}`, body)).data;
      return (await api.post("/invoice", body)).data;
    },
    onSuccess: (data) => {
      toast.success(isEdit ? "Invoice updated" : "Invoice saved");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["customers", orgId] });
      if (isNewCustomer) {
        setIsNewCustomer(false);
        setNewCustomer({
          name: "",
          company: "",
          phone: "",
          email: "",
          gst: "",
          address: "",
          city: "",
          state: "",
          state_code: "",
          country: "India",
          pincode: "",
        });
      }
      if (isNewShippedCustomer) {
        setIsNewShippedCustomer(false);
        setNewShippedCustomer({
          name: "",
          company: "",
          phone: "",
          email: "",
          gst: "",
          address: "",
          city: "",
          state: "",
          state_code: "",
          country: "India",
          pincode: "",
        });
      }
      nav(`/app/invoices/${data.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed to save"),
  });

  return (
    <div>
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Invoice</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter">
            Create invoice.
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="toggle-preview-button"
            onClick={() => {
              if (!selectedTemplateId) {
                toast.error("Select a template first");
                return;
              }
              setShowPreview((s) => !s);
            }}
            className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-sm hover:border-slate-900 dark:hover:border-white"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {showPreview ? "Hide preview" : "Preview"}
          </button>
          <button
            data-testid="save-invoice-button"
            onClick={() => save.mutate()}
            disabled={save.isPending || !selectedTemplateId}
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2.5 rounded-sm hover:bg-[#001F73] disabled:opacity-50 font-semibold"
          >
            <Save className="w-4 h-4" /> {save.isPending ? "Saving…" : "Save Invoice"}
          </button>
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 p-3 sm:p-4 rounded-sm mb-4">
        {/* Basic Invoice Info */}
        <div className="mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Invoice Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Organization</span>
              <select
                data-testid="invoice-org-select"
                value={orgId}
                onChange={(e) => {
                  setOrgId(e.target.value);
                  setInvoice({ ...invoice, customer_id: "", customer_id_shipped: "" });
                  setCustomerSearch("");
                }}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              >
                <option value="">Choose…</option>
                {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
              </select>
            </label>
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Template *</span>
              <select
                data-testid="invoice-template-select"
                value={selectedTemplateId || ""}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              >
                <option value="">Choose…</option>
                {templates.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
              {templates.length === 0 && (
                <div className="mt-1 text-[10px] text-amber-600">
                  No templates. <Link to="/app/templates" className="underline">Create one</Link>.
                </div>
              )}
            </label>
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Invoice #</span>
              <div className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-2 py-1.5 rounded-sm text-slate-700 dark:text-slate-300 text-sm">
                {isEdit ? generatedInvoiceNumber : "Auto-generated"}
              </div>
            </label>

          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Customer Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Bill To Customer */}
            <div>
              <div className="flex items-center justify-between">
                <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Bill To Customer</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-3 mb-2">
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="customer-type"
                    id="customer-type-existing"
                    checked={!isNewCustomer}
                    onChange={() => {
                      setIsNewCustomer(false);
                      setCustomerSearch("");
                    }}
                    className="accent-[#002FA7] cursor-pointer"
                  />
                  <label htmlFor="customer-type-existing" className="text-xs cursor-pointer">Existing</label>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="customer-type"
                    id="customer-type-new"
                    checked={isNewCustomer}
                    onChange={() => {
                      setIsNewCustomer(true);
                      setInvoice({ ...invoice, customer_id: "" });
                      setCustomerSearch("");
                    }}
                    className="accent-[#002FA7] cursor-pointer"
                  />
                  <label htmlFor="customer-type-new" className="text-xs cursor-pointer">New</label>
                </div>
              </div>
              {!isNewCustomer ? (
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search customer..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                  />
                  {showCustomerDropdown && customerSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#121214] border border-slate-300 dark:border-slate-700 rounded-sm max-h-48 overflow-auto shadow-lg">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-2 text-xs text-slate-500">No customers found</div>
                      ) : (
                        filteredCustomers.map((c) => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setInvoice({ ...invoice, customer_id: c.id });
                              setCustomerSearch(c.name);
                              setShowCustomerDropdown(false);
                            }}
                            className="p-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-200 dark:border-slate-700 last:border-0"
                          >
                            <div className="font-medium text-xs">{c.name}</div>
                            {c.company && <div className="text-[10px] text-slate-500">{c.company}</div>}
                            <div className="text-[10px] text-slate-400">{c.phone || c.email}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      placeholder="Customer Name *"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Contact Person"
                      value={newCustomer.company}
                      onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Phone"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="GST"
                      value={newCustomer.gst}
                      onChange={(e) => setNewCustomer({ ...newCustomer, gst: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Address"
                      value={newCustomer.address}
                      onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="City"
                      value={newCustomer.city}
                      onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="State"
                      value={newCustomer.state}
                      onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="State Code"
                      value={newCustomer.state_code}
                      onChange={(e) => setNewCustomer({ ...newCustomer, state_code: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Country"
                      value={newCustomer.country}
                      onChange={(e) => setNewCustomer({ ...newCustomer, country: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Pincode"
                      value={newCustomer.pincode}
                      onChange={(e) => setNewCustomer({ ...newCustomer, pincode: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Ship To Customer */}
            <div>
              <div className="flex items-center justify-between">
                <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Ship To Customer</span>
                {invoice.customer_id && (
                  <button
                    type="button"
                    onClick={() => {
                      const billToCustomer = customers.find((c) => c.id === invoice.customer_id);
                      if (billToCustomer) {
                        setInvoice({ ...invoice, customer_id_shipped: billToCustomer.id });
                        setShippedCustomerSearch(billToCustomer.name);
                        setIsNewShippedCustomer(false);
                      }
                    }}
                    className="text-[10px] text-[#002FA7] hover:underline"
                  >
                    Copy from Bill To
                  </button>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-3 mb-2">
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="shipped-customer-type"
                    id="shipped-customer-type-existing"
                    checked={!isNewShippedCustomer}
                    onChange={() => {
                      setIsNewShippedCustomer(false);
                      setShippedCustomerSearch("");
                    }}
                    className="accent-[#002FA7] cursor-pointer"
                  />
                  <label htmlFor="shipped-customer-type-existing" className="text-xs cursor-pointer">Existing</label>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="shipped-customer-type"
                    id="shipped-customer-type-new"
                    checked={isNewShippedCustomer}
                    onChange={() => {
                      setIsNewShippedCustomer(true);
                      setInvoice({ ...invoice, customer_id_shipped: "" });
                      setShippedCustomerSearch("");
                    }}
                    className="accent-[#002FA7] cursor-pointer"
                  />
                  <label htmlFor="shipped-customer-type-new" className="text-xs cursor-pointer">New</label>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="shipped-customer-type"
                    id="shipped-customer-type-same"
                    checked={!invoice.customer_id_shipped && !isNewShippedCustomer}
                    onChange={() => {
                      setIsNewShippedCustomer(false);
                      setInvoice({ ...invoice, customer_id_shipped: "" });
                      setShippedCustomerSearch("");
                    }}
                    className="accent-[#002FA7] cursor-pointer"
                  />
                  <label htmlFor="shipped-customer-type-same" className="text-xs cursor-pointer">Same as Bill To</label>
                </div>
              </div>
              {!isNewShippedCustomer ? (
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search customer..."
                    value={shippedCustomerSearch}
                    onChange={(e) => {
                      setShippedCustomerSearch(e.target.value);
                      setShowShippedCustomerDropdown(true);
                    }}
                    onFocus={() => setShowShippedCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowShippedCustomerDropdown(false), 200)}
                    disabled={!invoice.customer_id_shipped}
                    className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm disabled:opacity-50"
                  />
                  {showShippedCustomerDropdown && shippedCustomerSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#121214] border border-slate-300 dark:border-slate-700 rounded-sm max-h-48 overflow-auto shadow-lg">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-2 text-xs text-slate-500">No customers found</div>
                      ) : (
                        filteredCustomers.map((c) => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setInvoice({ ...invoice, customer_id_shipped: c.id });
                              setShippedCustomerSearch(c.name);
                              setShowShippedCustomerDropdown(false);
                            }}
                            className="p-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-200 dark:border-slate-700 last:border-0"
                          >
                            <div className="font-medium text-xs">{c.name}</div>
                            {c.company && <div className="text-[10px] text-slate-500">{c.company}</div>}
                            <div className="text-[10px] text-slate-400">{c.phone || c.email}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      placeholder="Customer Name *"
                      value={newShippedCustomer.name}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, name: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Contact Person"
                      value={newShippedCustomer.company}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, company: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Phone"
                      value={newShippedCustomer.phone}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, phone: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Email"
                      value={newShippedCustomer.email}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, email: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="GST"
                      value={newShippedCustomer.gst}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, gst: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Address"
                      value={newShippedCustomer.address}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, address: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="City"
                      value={newShippedCustomer.city}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, city: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="State"
                      value={newShippedCustomer.state}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, state: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="State Code"
                      value={newShippedCustomer.state_code}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, state_code: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Country"
                      value={newShippedCustomer.country}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, country: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                    <input
                      placeholder="Pincode"
                      value={newShippedCustomer.pincode}
                      onChange={(e) => setNewShippedCustomer({ ...newShippedCustomer, pincode: e.target.value })}
                      className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dates & Settings */}
        <div className="mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Dates & Settings</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Invoice Date</span>
              <input
                type="date"
                data-testid="invoice-date-input"
                value={invoice.invoice_date}
                onChange={(e) => setInvoice({ ...invoice, invoice_date: e.target.value })}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              />
            </label>
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Due Date</span>
              <input
                type="date"
                data-testid="invoice-due-input"
                value={invoice.due_date}
                onChange={(e) => setInvoice({ ...invoice, due_date: e.target.value })}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              />
            </label>
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Reverse Charge</span>
              <select
                data-testid="invoice-reverse-charge"
                value={invoice.reverse_charge ? "yes" : "no"}
                onChange={(e) => setInvoice({ ...invoice, reverse_charge: e.target.value === "yes" })}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
          </div>
        </div>

        {/* More Details */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">More Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Transport</span>
              <input
                type="text"
                data-testid="invoice-transport"
                value={invoice.transport}
                onChange={(e) => setInvoice({ ...invoice, transport: e.target.value })}
                placeholder="Mode"
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              />
            </label>
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">E-Way Bill</span>
              <input
                type="text"
                data-testid="invoice-e-way-bill"
                value={invoice.e_way_bill_no}
                onChange={(e) => setInvoice({ ...invoice, e_way_bill_no: e.target.value })}
                placeholder="Number"
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              />
            </label>
            <label>
              <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">IRN</span>
              <input
                type="text"
                data-testid="invoice-irn"
                value={invoice.irn_number}
                onChange={(e) => setInvoice({ ...invoice, irn_number: e.target.value })}
                placeholder="Number"
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Items table */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-sm mb-4 overflow-x-auto">
        <table className="w-full text-xs min-w-[600px]">
          <thead className="bg-slate-50 dark:bg-slate-900 text-left uppercase text-[9px] tracking-[0.15em] text-slate-500">
            <tr>
              {itemColumns.map((col) => {
                const columnLabels = {
                  item_name: "Item",
                  description: "Description",
                  hsn_sac: "HSN/SAC",
                  quantity: "Qty",
                  unit: "Unit",
                  price: "Price",
                  amount: "Amount",
                };
                return (
                  <th key={col} className="p-3">
                    {columnLabels[col] || col}
                  </th>
                );
              })}
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((r, idx) => (
              <tr key={r.id} className="border-t border-slate-200/60 dark:border-slate-800/60" data-testid={`item-row-${idx}`}>
                {itemColumns.map((col) => {
                  if (col === "item_name") {
                    return (
                      <td key={col} className="p-2 relative">
                        <div className="flex gap-2">
                          <div className="relative w-48" ref={el => inventoryDropdownRefs.current[idx] = el}>
                            <input
                              data-testid={`item-inventory-search-${idx}`}
                              type="text"
                              value={inventorySearches[idx] || ""}
                              onChange={(e) => {
                                setInventorySearches({ ...inventorySearches, [idx]: e.target.value });
                                setShowInventoryDropdowns({ ...showInventoryDropdowns, [idx]: true });
                              }}
                              onFocus={() => setShowInventoryDropdowns({ ...showInventoryDropdowns, [idx]: true })}
                              placeholder="Search SKU or name..."
                              className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm"
                            />
                            {showInventoryDropdowns[idx] && inventoryDropdownPositions[idx] && createPortal(
                              <div
                                ref={el => inventoryDropdownContainerRefs.current[idx] = el}
                                className="fixed z-50 bg-white dark:bg-[#121214] border border-slate-300 dark:border-slate-700 rounded-sm shadow-lg max-h-48 overflow-y-auto"
                                style={{
                                  top: `${inventoryDropdownPositions[idx].top}px`,
                                  left: `${inventoryDropdownPositions[idx].left}px`,
                                  width: `${inventoryDropdownPositions[idx].width}px`
                                }}
                              >
                                {inventory
                                  .filter(inv =>
                                    (inv.item_name?.toLowerCase().includes((inventorySearches[idx] || "").toLowerCase()) ||
                                     inv.sku?.toLowerCase().includes((inventorySearches[idx] || "").toLowerCase())) &&
                                    !invoice.items.some((item, i) => i !== idx && item.inventory_item_id === inv.id)
                                  )
                                  .slice(0, 10)
                                  .map((inv) => (
                                    <div
                                      key={inv.id}
                                      onClick={() => {
                                        setItem(idx, {
                                          inventory_item_id: inv.id,
                                          item_name: inv.item_name,
                                          description: inv.description || "",
                                          price: inv.price || "",
                                          unit: inv.unit || "",
                                          hsn_sac: inv.hsn_sac || "",
                                        });
                                        setInventorySearches({ ...inventorySearches, [idx]: "" });
                                        setShowInventoryDropdowns({ ...showInventoryDropdowns, [idx]: false });
                                      }}
                                      className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-xs"
                                    >
                                      <div className="font-medium">{inv.item_name}</div>
                                      <div className={`${
                                        parseFloat(inv.quantity) < 0 ? 'text-red-600 dark:text-red-500' :
                                        parseFloat(inv.quantity) <= parseFloat(inv.low_stock_threshold) ? 'text-amber-600 dark:text-amber-500' :
                                        'text-green-600 dark:text-green-500'
                                      }`}>
                                        SKU: {inv.sku} | Stock: {inv.quantity} {inv.unit}
                                      </div>
                                    </div>
                                  ))}
                                {inventory.filter(inv =>
                                  (inv.item_name?.toLowerCase().includes((inventorySearches[idx] || "").toLowerCase()) ||
                                   inv.sku?.toLowerCase().includes((inventorySearches[idx] || "").toLowerCase())) &&
                                  !invoice.items.some((item, i) => i !== idx && item.inventory_item_id === inv.id)
                                ).length === 0 && (
                                  <div className="px-3 py-2 text-slate-500 text-xs">No items found</div>
                                )}
                              </div>,
                              document.body
                            )}
                          </div>
                          <input
                            data-testid={`item-name-${idx}`}
                            value={r.item_name}
                            onChange={(e) => setItem(idx, { item_name: e.target.value })}
                            className="flex-1 border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm"
                            placeholder="Item name"
                          />
                        </div>
                      </td>
                    );
                  } else if (col === "hsn_sac") {
                    return (
                      <td key={col} className="p-2 w-24">
                        <input data-testid={`item-hsn-${idx}`} value={r.hsn_sac} onChange={(e) => setItem(idx, { hsn_sac: e.target.value })} placeholder="HSN/SAC" className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm" />
                      </td>
                    );
                  } else if (col === "description") {
                    return (
                      <td key={col} className="p-2">
                        <input data-testid={`item-desc-${idx}`} value={r.description} onChange={(e) => setItem(idx, { description: e.target.value })} className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm" />
                      </td>
                    );
                  } else if (col === "quantity") {
                    return (
                      <td key={col} className="p-2 w-20">
                        <input type="number" data-testid={`item-qty-${idx}`} value={r.quantity || ''} onChange={(e) => setItem(idx, { quantity: e.target.value === '' ? 0 : +e.target.value })} onWheel={(e) => e.target.blur()} placeholder="Qty" className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm" />
                      </td>
                    );
                  } else if (col === "unit") {
                    return (
                      <td key={col} className="p-2 w-20">
                        <input data-testid={`item-unit-${idx}`} value={r.unit} onChange={(e) => setItem(idx, { unit: e.target.value })} placeholder="kg" className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm" />
                      </td>
                    );
                  } else if (col === "price") {
                    return (
                      <td key={col} className="p-2 w-28">
                        <input type="number" data-testid={`item-price-${idx}`} value={r.price || ''} onChange={(e) => setItem(idx, { price: e.target.value === '' ? 0 : +e.target.value })} onWheel={(e) => e.target.blur()} placeholder="Price" className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm" />
                      </td>
                    );
                  } else if (col === "amount") {
                    return (
                      <td key={col} className="p-2 text-right font-semibold">₹{Number(r.amount || 0).toLocaleString("en-IN")}</td>
                    );
                  }
                  return null;
                })}
                <td className="p-2">
                  <button onClick={() => removeItem(idx)} disabled={invoice.items.length === 1} className="p-2 hover:text-red-600 disabled:opacity-30" data-testid={`remove-item-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <button onClick={addItem} className="inline-flex items-center gap-2 text-sm hover:text-[#002FA7]" data-testid="add-item-button">
            <Plus className="w-4 h-4" /> Add row
          </button>
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 p-3 sm:p-4 rounded-sm mb-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Adjustments</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label>
            <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Discount (%)</span>
            <input
              type="number"
              data-testid="invoice-discount-input"
              value={invoice.discount || ''}
              onChange={(e) => setInvoice({ ...invoice, discount: e.target.value === '' ? 0 : +e.target.value })}
              onWheel={(e) => e.target.blur()}
              placeholder="Discount %"
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
            />
          </label>
          <label>
            <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Shipping</span>
            <input
              type="number"
              data-testid="invoice-shipping-input"
              value={invoice.shipping || ''}
              onChange={(e) => setInvoice({ ...invoice, shipping: e.target.value === '' ? 0 : +e.target.value })}
              onWheel={(e) => e.target.blur()}
              placeholder="Shipping"
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
            />
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label>
            <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">CGST (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              data-testid="invoice-cgst-input"
              value={invoice.cgst || ''}
              onChange={(e) => {
                const val = e.target.value === '' ? 0 : Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                setInvoice({ ...invoice, cgst: val });
              }}
              onWheel={(e) => e.target.blur()}
              placeholder="CGST %"
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
            />
          </label>
          <label>
            <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">SGST (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              data-testid="invoice-sgst-input"
              value={invoice.sgst || ''}
              onChange={(e) => {
                const val = e.target.value === '' ? 0 : Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                setInvoice({ ...invoice, sgst: val });
              }}
              onWheel={(e) => e.target.blur()}
              placeholder="SGST %"
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
            />
          </label>
          <label>
            <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">IGST (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              data-testid="invoice-igst-input"
              value={invoice.igst || ''}
              onChange={(e) => {
                const val = e.target.value === '' ? 0 : Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                setInvoice({ ...invoice, igst: val });
              }}
              onWheel={(e) => e.target.blur()}
              placeholder="IGST %"
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm"
            />
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 mt-3">
          <div><div className="text-[10px] text-slate-500">Subtotal</div><div className="text-sm font-semibold">₹{totals.subtotal.toLocaleString("en-IN")}</div></div>
          {totals.discount > 0 && <div><div className="text-[10px] text-slate-500">Discount ({totals.discountPercent}%)</div><div className="text-sm font-semibold">-₹{totals.discount.toLocaleString("en-IN")}</div></div>}
          {totals.shipping > 0 && <div><div className="text-[10px] text-slate-500">Shipping</div><div className="text-sm font-semibold">+₹{totals.shipping.toLocaleString("en-IN")}</div></div>}
          <div><div className="text-[10px] text-slate-500">Total Taxes</div><div className="text-sm font-semibold">₹{totals.totalTaxes.toLocaleString("en-IN")}</div></div>
          <div><div className="text-[10px] text-slate-500">Pre-roundoff Total</div><div className="text-sm font-semibold">₹{(totals.subtotal - totals.discount + totals.shipping + totals.totalTaxes).toLocaleString("en-IN")}</div></div>
          <div>
            <div className="text-[10px] text-slate-500">Round Off</div>
            <div className="text-sm font-semibold">+₹{totals.roundoff.toLocaleString("en-IN")}</div>
          </div>
          <div className="col-span-2 border-t border-slate-200 dark:border-slate-800 pt-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Total</div>
            <div className="text-xl sm:text-2xl font-extrabold tracking-tighter text-[#002FA7]" data-testid="invoice-total">
              ₹{totals.total.toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <label>
          <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Notes</span>
          <textarea data-testid="invoice-notes-input" rows={2} value={invoice.notes} onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm" />
        </label>
        <label>
          <span className="uppercase text-[9px] tracking-[0.15em] text-slate-500">Terms</span>
          <textarea data-testid="invoice-terms-input" rows={2} value={invoice.terms} onChange={(e) => setInvoice({ ...invoice, terms: e.target.value })} className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 rounded-sm text-sm" />
        </label>
      </div>

      {/* Preview - Also used for PDF */}
      <div style={{ position: showPreview ? "static" : "fixed", left: showPreview ? undefined : "-99999px", top: 0 }}>
        <div className="mb-4 uppercase text-[10px] tracking-[0.3em] text-slate-500">Live preview</div>
        <div className="overflow-auto bg-slate-100 dark:bg-slate-900 p-6 rounded-sm">
          <InvoiceDocument
            ref={docRef}
            model={previewModel}
            showToolbar={false}
          />
        </div>
      </div>
    </div>
  );
}
